import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Circle, Ellipse, RegularPolygon, Text, Transformer } from 'react-konva';
import Konva from 'konva';
import { apiFetch } from '../utils/api';
import { isPosModuleEnabled } from '../config/featureFlags';
import '../App.css';
import './PosFloorAdmin.css';

type PosSite = { siteId: string; label: string };
type FloorTableShape = 'rect' | 'roundRect' | 'circle' | 'ellipse' | 'diamond';
type LayoutMode = 'station' | 'handheld';
type LayoutGeom = {
  posX: number;
  posY: number;
  width: number;
  height: number;
  rotation: number;
};
type FloorTable = {
  id: number;
  areaId: number;
  name: string;
  seats: number;
  sortOrder: number;
  posX: number;
  posY: number;
  width: number;
  height: number;
  shape: FloorTableShape;
  rotation: number;
  hhX: number;
  hhY: number;
  hhWidth: number;
  hhHeight: number;
  hhRotation: number;
  layouts?: { station: LayoutGeom; handheld: LayoutGeom };
  active: boolean;
};
type FloorDecor = {
  id: number;
  areaId: number;
  name: string;
  decorType: 'line' | 'wall' | 'label' | 'rect' | 'outline' | string;
  posX: number;
  posY: number;
  width: number;
  height: number;
  rotation: number;
  hhX: number;
  hhY: number;
  hhWidth: number;
  hhHeight: number;
  hhRotation: number;
  layouts?: { station: LayoutGeom; handheld: LayoutGeom };
  sortOrder: number;
  active: boolean;
};
type FloorArea = {
  id: number;
  name: string;
  seatingMode: 'tables' | 'named_tabs';
  sortOrder: number;
  active: boolean;
  tables: FloorTable[];
  decor?: FloorDecor[];
};
type FloorOverview = {
  siteId: string;
  areas: FloorArea[];
  totals: { areas: number; tables: number; seats: number; decor?: number };
};

type EditTarget =
  | { kind: 'area'; area: FloorArea }
  | { kind: 'table'; table: FloorTable; areaName: string }
  | { kind: 'new-area' }
  | { kind: 'new-table'; areaId: number; areaName: string };

const CANVAS_BY_LAYOUT: Record<LayoutMode, { w: number; h: number }> = {
  station: { w: 1100, h: 480 },
  handheld: { w: 400, h: 920 },
};

function viewGeom(
  t: {
    posX: number;
    posY: number;
    width: number;
    height: number;
    rotation: number;
    hhX: number;
    hhY: number;
    hhWidth: number;
    hhHeight: number;
    hhRotation: number;
  },
  mode: LayoutMode
): LayoutGeom {
  if (mode === 'handheld') {
    return {
      posX: t.hhX,
      posY: t.hhY,
      width: t.hhWidth,
      height: t.hhHeight,
      rotation: t.hhRotation,
    };
  }
  return {
    posX: t.posX,
    posY: t.posY,
    width: t.width,
    height: t.height,
    rotation: t.rotation,
  };
}

/** Map view-space geometry edits onto station or hh* fields. */
function geomPatch(mode: LayoutMode, g: Partial<LayoutGeom>): Partial<FloorTable> {
  if (mode === 'handheld') {
    const p: Partial<FloorTable> = {};
    if (g.posX != null) p.hhX = g.posX;
    if (g.posY != null) p.hhY = g.posY;
    if (g.width != null) p.hhWidth = g.width;
    if (g.height != null) p.hhHeight = g.height;
    if (g.rotation != null) p.hhRotation = g.rotation;
    return p;
  }
  return { ...g };
}

function normalizeTable(t: FloorTable): FloorTable {
  const posX = t.posX ?? t.layouts?.station?.posX ?? 0;
  const posY = t.posY ?? t.layouts?.station?.posY ?? 0;
  const width = t.width ?? t.layouts?.station?.width ?? 72;
  const height = t.height ?? t.layouts?.station?.height ?? 72;
  const rotation = t.rotation ?? t.layouts?.station?.rotation ?? 0;
  const hhX = t.hhX ?? t.layouts?.handheld?.posX ?? posX;
  const hhY = t.hhY ?? t.layouts?.handheld?.posY ?? posY;
  const hhWidth = t.hhWidth ?? t.layouts?.handheld?.width ?? width;
  const hhHeight = t.hhHeight ?? t.layouts?.handheld?.height ?? height;
  const hhRotation = t.hhRotation ?? t.layouts?.handheld?.rotation ?? rotation;
  return {
    ...t,
    posX,
    posY,
    width,
    height,
    rotation,
    hhX,
    hhY,
    hhWidth,
    hhHeight,
    hhRotation,
    shape: t.shape || 'rect',
    layouts: t.layouts || {
      station: { posX, posY, width, height, rotation },
      handheld: { posX: hhX, posY: hhY, width: hhWidth, height: hhHeight, rotation: hhRotation },
    },
  };
}

function normalizeDecor(d: FloorDecor): FloorDecor {
  const posX = d.posX ?? d.layouts?.station?.posX ?? 0;
  const posY = d.posY ?? d.layouts?.station?.posY ?? 0;
  const width = d.width ?? d.layouts?.station?.width ?? 40;
  const height = d.height ?? d.layouts?.station?.height ?? 40;
  const rotation = d.rotation ?? d.layouts?.station?.rotation ?? 0;
  const hhX = d.hhX ?? d.layouts?.handheld?.posX ?? posX;
  const hhY = d.hhY ?? d.layouts?.handheld?.posY ?? posY;
  const hhWidth = d.hhWidth ?? d.layouts?.handheld?.width ?? width;
  const hhHeight = d.hhHeight ?? d.layouts?.handheld?.height ?? height;
  const hhRotation = d.hhRotation ?? d.layouts?.handheld?.rotation ?? rotation;
  return {
    ...d,
    posX,
    posY,
    width,
    height,
    rotation,
    hhX,
    hhY,
    hhWidth,
    hhHeight,
    hhRotation,
  };
}

function useIsPhone() {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setPhone(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return phone;
}

const PosFloorAdmin: React.FC = () => {
  const isPhone = useIsPhone();
  const [sites, setSites] = useState<PosSite[]>([]);
  const [siteId, setSiteId] = useState('TAPROOM-MADISON');
  const [floor, setFloor] = useState<FloorOverview | null>(null);
  const [activeAreaId, setActiveAreaId] = useState<number | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('station');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<EditTarget | null>(null);
  const [draft, setDraft] = useState({
    name: '',
    seats: '2',
    seatingMode: 'tables' as 'tables' | 'named_tabs',
    active: true,
    posX: '40',
    posY: '40',
    width: '72',
    height: '72',
    shape: 'rect' as FloorTableShape,
    rotation: '0',
  });
  const [localTables, setLocalTables] = useState<FloorTable[]>([]);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [dirtyLayout, setDirtyLayout] = useState(false);
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const shapeRefs = useRef<Record<number, Konva.Node>>({});

  const canvasW = CANVAS_BY_LAYOUT[layoutMode].w;
  const canvasH = CANVAS_BY_LAYOUT[layoutMode].h;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [statusRes, floorRes] = await Promise.all([
        apiFetch('/api/pos/status'),
        apiFetch(`/api/pos/floor?siteId=${encodeURIComponent(siteId)}`),
      ]);
      const status = await statusRes.json();
      const data = await floorRes.json();
      setSites(Array.isArray(status.sites) ? status.sites : []);
      const areas: FloorArea[] = Array.isArray(data.areas)
        ? data.areas.map((a: FloorArea) => ({
            ...a,
            tables: (a.tables || []).map(normalizeTable),
            decor: (a.decor || []).map(normalizeDecor),
          }))
        : [];
      setFloor({ ...data, areas });
      setActiveAreaId((prev) => {
        if (prev && areas.some((a) => a.id === prev)) return prev;
        return areas[0]?.id ?? null;
      });
      setDirtyLayout(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load floor');
    }
  }, [siteId]);

  useEffect(() => {
    if (isPosModuleEnabled()) load();
  }, [load]);

  const activeArea = useMemo(
    () => (floor?.areas || []).find((a) => a.id === activeAreaId) || null,
    [floor, activeAreaId]
  );
  const decorElements = useMemo(
    () => (activeArea?.decor || []).filter((d) => d.active !== false).map(normalizeDecor),
    [activeArea]
  );

  useEffect(() => {
    setLocalTables(activeArea?.tables ? activeArea.tables.map(normalizeTable) : []);
    setSelectedTableId(null);
    setDirtyLayout(false);
  }, [activeAreaId, activeArea?.id, floor]);

  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr || typeof tr.nodes !== 'function') return;
    if (!selectedTableId) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const node = shapeRefs.current[selectedTableId];
    if (node) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
    }
  }, [selectedTableId, localTables, layoutMode]);

  if (!isPosModuleEnabled()) {
    return (
      <div className="page-container container">
        <h2>POS Floor</h2>
        <p>The POS module is turned off.</p>
      </div>
    );
  }

  const siteOptions =
    sites.length > 0
      ? sites
      : [
          { siteId: 'TAPROOM-MADISON', label: 'Madison' },
          { siteId: 'TAPROOM-ATHENS', label: 'Athens' },
        ];

  const switchLayoutMode = (mode: LayoutMode) => {
    if (mode === layoutMode) return;
    // Prefer discarding local edits and reloading from floor data for the new mode
    setLocalTables(activeArea?.tables ? activeArea.tables.map(normalizeTable) : []);
    setSelectedTableId(null);
    setDirtyLayout(false);
    setLayoutMode(mode);
  };

  const openArea = (area: FloorArea) => {
    setEdit({ kind: 'area', area });
    setDraft({
      name: area.name,
      seats: '2',
      seatingMode: area.seatingMode,
      active: area.active,
      posX: '40',
      posY: '40',
      width: '72',
      height: '72',
      shape: 'rect',
      rotation: '0',
    });
  };

  const openTable = (table: FloorTable, areaName: string) => {
    const g = viewGeom(table, layoutMode);
    setEdit({ kind: 'table', table, areaName });
    setDraft({
      name: table.name,
      seats: String(table.seats),
      seatingMode: 'tables',
      active: table.active,
      posX: String(g.posX),
      posY: String(g.posY),
      width: String(g.width),
      height: String(g.height),
      shape: table.shape || 'rect',
      rotation: String(g.rotation || 0),
    });
  };

  const openNewArea = () => {
    setEdit({ kind: 'new-area' });
    setDraft({
      name: '',
      seats: '2',
      seatingMode: 'tables',
      active: true,
      posX: '40',
      posY: '40',
      width: '72',
      height: '72',
      shape: 'rect',
      rotation: '0',
    });
  };

  const openNewTable = (areaId: number, areaName: string) => {
    setEdit({ kind: 'new-table', areaId, areaName });
    setDraft({
      name: '',
      seats: '4',
      seatingMode: 'tables',
      active: true,
      posX: '40',
      posY: '40',
      width: '72',
      height: '72',
      shape: 'rect',
      rotation: '0',
    });
  };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      if (edit.kind === 'new-area') {
        if (!draft.name.trim()) throw new Error('Area name is required');
        await apiFetch('/api/pos/areas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            siteId,
            name: draft.name.trim(),
            seatingMode: draft.seatingMode,
          }),
        });
        setSuccess('Area created');
      } else if (edit.kind === 'area') {
        await apiFetch(`/api/pos/areas/${edit.area.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: draft.name.trim(),
            seatingMode: draft.seatingMode,
            active: draft.active,
            sortOrder: edit.area.sortOrder,
          }),
        });
        setSuccess('Area updated');
      } else if (edit.kind === 'new-table') {
        if (!draft.name.trim()) throw new Error('Table name is required');
        const geom = {
          posX: Number(draft.posX) || 0,
          posY: Number(draft.posY) || 0,
          width: Number(draft.width) || 72,
          height: Number(draft.height) || 72,
          rotation: Number(draft.rotation) || 0,
        };
        // createTable accepts flat station + hh* (not layout:); seed the active variant
        const body =
          layoutMode === 'handheld'
            ? {
                siteId,
                areaId: edit.areaId,
                name: draft.name.trim(),
                seats: Number(draft.seats) || 1,
                shape: draft.shape,
                posX: 40,
                posY: 40,
                width: 72,
                height: 72,
                rotation: 0,
                hhX: geom.posX,
                hhY: geom.posY,
                hhWidth: geom.width,
                hhHeight: geom.height,
                hhRotation: geom.rotation,
              }
            : {
                siteId,
                areaId: edit.areaId,
                name: draft.name.trim(),
                seats: Number(draft.seats) || 1,
                shape: draft.shape,
                ...geom,
              };
        await apiFetch('/api/pos/tables', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        setSuccess('Table created');
      } else {
        await apiFetch(`/api/pos/tables/${edit.table.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: draft.name.trim(),
            seats: Number(draft.seats) || 1,
            active: draft.active,
            layout: layoutMode,
            posX: Number(draft.posX) || 0,
            posY: Number(draft.posY) || 0,
            width: Number(draft.width) || 72,
            height: Number(draft.height) || 72,
            shape: draft.shape,
            rotation: Number(draft.rotation) || 0,
          }),
        });
        setSuccess('Table updated');
      }
      setEdit(null);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const updateLocalTable = (id: number, patch: Partial<FloorTable>) => {
    setLocalTables((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    setDirtyLayout(true);
  };

  const saveLayout = async () => {
    if (!localTables.length) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/pos/floor/${encodeURIComponent(siteId)}/layout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layout: layoutMode,
          tables: localTables.map((t) => {
            const g = viewGeom(t, layoutMode);
            return {
              id: t.id,
              posX: g.posX,
              posY: g.posY,
              width: g.width,
              height: g.height,
              shape: t.shape,
              rotation: g.rotation,
            };
          }),
        }),
      });
      setSuccess(
        layoutMode === 'handheld' ? 'Handheld floor plan saved' : 'Station floor plan saved'
      );
      setDirtyLayout(false);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save layout');
    } finally {
      setBusy(false);
    }
  };

  const scale = isPhone
    ? Math.min(1, (typeof window !== 'undefined' ? window.innerWidth - 32 : 360) / canvasW)
    : 1;

  return (
    <div className="page-container container pos-floor-admin">
      <h2>POS Floor</h2>
      <p className="text-muted">
        Sections swipe/tile like Facility View. Tables have floor-plan position and size for the
        Android app. Drag on desktop; on phone use the list and position fields. Station is
        landscape; Handheld is portrait.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="row g-2 align-items-end" style={{ marginBottom: 16 }}>
        <div className="col-md-3">
          <label className="form-label">Site</label>
          <select
            className="form-select"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
          >
            {siteOptions.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <button type="button" className="btn btn-primary w-100" onClick={openNewArea} disabled={busy}>
            Add area
          </button>
        </div>
        <div className="col-md-6 text-muted" style={{ paddingBottom: 6 }}>
          {floor
            ? `${floor.totals.areas} areas · ${floor.totals.tables} tables · ${floor.totals.seats} seats`
            : 'Loading…'}
        </div>
      </div>

      {/* Section tiles (display order) */}
      <div className="pos-floor-section-tiles" data-testid="pos-floor-sections">
        {(floor?.areas || []).map((area) => (
          <button
            type="button"
            key={area.id}
            className={`pos-floor-section-tile${area.id === activeAreaId ? ' is-active' : ''}`}
            onClick={() => setActiveAreaId(area.id)}
          >
            <span className="pos-floor-section-tile__name">{area.name}</span>
            <span className="pos-floor-section-tile__meta">
              {area.seatingMode === 'named_tabs' ? 'Tabs' : `${area.tables.length} tables`}
            </span>
          </button>
        ))}
      </div>

      {activeArea && (
        <div className="pos-floor-area-toolbar">
          <div>
            <strong>{activeArea.name}</strong>{' '}
            <span className="text-muted">
              {activeArea.seatingMode === 'named_tabs' ? 'Named tabs' : 'Tables'} · order{' '}
              {activeArea.sortOrder}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {activeArea.seatingMode === 'tables' && (
              <div
                className="pos-floor-layout-toggle"
                role="group"
                aria-label="Floor layout mode"
              >
                <button
                  type="button"
                  className={layoutMode === 'station' ? 'is-active' : ''}
                  aria-pressed={layoutMode === 'station'}
                  onClick={() => switchLayoutMode('station')}
                >
                  Station
                </button>
                <button
                  type="button"
                  className={layoutMode === 'handheld' ? 'is-active' : ''}
                  aria-pressed={layoutMode === 'handheld'}
                  onClick={() => switchLayoutMode('handheld')}
                >
                  Handheld
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => openArea(activeArea)}
            >
              Edit area
            </button>
            {activeArea.seatingMode === 'tables' && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => openNewTable(activeArea.id, activeArea.name)}
                >
                  Add table
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  disabled={!dirtyLayout || busy}
                  onClick={saveLayout}
                >
                  Save floor plan
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {activeArea?.seatingMode === 'named_tabs' && (
        <p className="text-muted">Guests open tabs by name in this section (no table shapes).</p>
      )}

      {/* Desktop / tablet canvas */}
      {activeArea?.seatingMode === 'tables' && (
        <div
          className={`pos-floor-canvas-wrap${layoutMode === 'handheld' ? ' is-handheld' : ''}`}
          data-testid="pos-floor-canvas"
          data-layout={layoutMode}
        >
          <Stage
            width={canvasW * scale}
            height={canvasH * scale}
            scaleX={scale}
            scaleY={scale}
            style={{
              background: '#f3f4f6',
              borderRadius: 8,
              width: '100%',
              maxWidth: canvasW,
            }}
            onMouseDown={(e) => {
              if (e.target === e.target.getStage()) setSelectedTableId(null);
            }}
          >
            <Layer>
              <Rect x={0} y={0} width={canvasW} height={canvasH} fill="#f3f4f6" listening={false} />
              {decorElements.map((d) => {
                const g = viewGeom(d, layoutMode);
                const stroke = '#9ca3af';
                if (d.decorType === 'label') {
                  return (
                    <Text
                      key={`decor-${d.id}`}
                      x={g.posX}
                      y={g.posY}
                      width={Math.max(g.width, 40)}
                      text={d.name}
                      fontSize={14}
                      fill="#6b7280"
                      listening={false}
                    />
                  );
                }
                if (d.decorType === 'outline') {
                  return (
                    <React.Fragment key={`decor-${d.id}`}>
                      <Rect
                        x={g.posX}
                        y={g.posY}
                        width={g.width}
                        height={g.height}
                        rotation={g.rotation || 0}
                        fillEnabled={false}
                        stroke={stroke}
                        strokeWidth={3}
                        listening={false}
                      />
                      <Text
                        x={g.posX}
                        y={g.posY - 16}
                        width={g.width}
                        align="center"
                        text={d.name}
                        fontSize={11}
                        fill="#9ca3af"
                        listening={false}
                      />
                    </React.Fragment>
                  );
                }
                // line / wall / rect
                return (
                  <React.Fragment key={`decor-${d.id}`}>
                    <Rect
                      x={g.posX}
                      y={g.posY}
                      width={g.width}
                      height={g.height}
                      rotation={g.rotation || 0}
                      fill={d.decorType === 'line' || d.decorType === 'wall' ? '#d1d5db' : '#e5e7eb'}
                      stroke={stroke}
                      strokeWidth={d.decorType === 'rect' ? 2 : 0}
                      listening={false}
                    />
                    {d.decorType === 'rect' && (
                      <Text
                        x={g.posX}
                        y={g.posY + g.height / 2 - 8}
                        width={g.width}
                        align="center"
                        text={d.name}
                        fontSize={13}
                        fontStyle="bold"
                        fill="#6b7280"
                        listening={false}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              {localTables.map((t) => {
                const g = viewGeom(t, layoutMode);
                const common = {
                  id: `table-${t.id}`,
                  x: g.posX,
                  y: g.posY,
                  rotation: g.rotation || 0,
                  draggable: true,
                  opacity: t.active ? 1 : 0.45,
                  onClick: () => setSelectedTableId(t.id),
                  onTap: () => setSelectedTableId(t.id),
                  onDragEnd: (evt: Konva.KonvaEventObject<DragEvent>) => {
                    updateLocalTable(
                      t.id,
                      geomPatch(layoutMode, {
                        posX: Math.round(evt.target.x()),
                        posY: Math.round(evt.target.y()),
                      })
                    );
                  },
                  onTransformEnd: (evt: Konva.KonvaEventObject<Event>) => {
                    const node = evt.target;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();
                    node.scaleX(1);
                    node.scaleY(1);
                    if (t.shape === 'circle') {
                      const r = Math.max(16, (node as Konva.Circle).radius() * scaleX);
                      updateLocalTable(
                        t.id,
                        geomPatch(layoutMode, {
                          posX: Math.round(node.x()),
                          posY: Math.round(node.y()),
                          width: Math.round(r * 2),
                          height: Math.round(r * 2),
                          rotation: Math.round(node.rotation()),
                        })
                      );
                    } else if (t.shape === 'diamond') {
                      const poly = node as Konva.RegularPolygon;
                      const nextR = Math.max(16, poly.radius() * scaleX);
                      const size = Math.round(nextR * Math.SQRT2);
                      updateLocalTable(
                        t.id,
                        geomPatch(layoutMode, {
                          posX: Math.round(poly.x() - size / 2),
                          posY: Math.round(poly.y() - size / 2),
                          width: size,
                          height: size,
                          rotation: Math.round(poly.rotation()),
                        })
                      );
                    } else {
                      updateLocalTable(
                        t.id,
                        geomPatch(layoutMode, {
                          posX: Math.round(node.x()),
                          posY: Math.round(node.y()),
                          width: Math.max(16, Math.round((node.width?.() || g.width) * scaleX)),
                          height: Math.max(16, Math.round((node.height?.() || g.height) * scaleY)),
                          rotation: Math.round(node.rotation()),
                        })
                      );
                    }
                  },
                  ref: (node: Konva.Node | null) => {
                    if (node) shapeRefs.current[t.id] = node;
                  },
                };
                if (t.shape === 'circle') {
                  const r = Math.max(g.width, g.height) / 2;
                  return (
                    <React.Fragment key={t.id}>
                      <Circle
                        {...common}
                        x={g.posX + r}
                        y={g.posY + r}
                        radius={r}
                        fill="#fff"
                        stroke={selectedTableId === t.id ? '#2563eb' : '#374151'}
                        strokeWidth={2}
                        onDragEnd={(evt: Konva.KonvaEventObject<DragEvent>) => {
                          const node = evt.target;
                          updateLocalTable(
                            t.id,
                            geomPatch(layoutMode, {
                              posX: Math.round(node.x() - r),
                              posY: Math.round(node.y() - r),
                            })
                          );
                        }}
                        onTransformEnd={(evt: Konva.KonvaEventObject<Event>) => {
                          const node = evt.target as Konva.Circle;
                          const scaleX = node.scaleX();
                          node.scaleX(1);
                          node.scaleY(1);
                          const nextR = Math.max(16, node.radius() * scaleX);
                          updateLocalTable(
                            t.id,
                            geomPatch(layoutMode, {
                              posX: Math.round(node.x() - nextR),
                              posY: Math.round(node.y() - nextR),
                              width: Math.round(nextR * 2),
                              height: Math.round(nextR * 2),
                              rotation: Math.round(node.rotation()),
                            })
                          );
                        }}
                      />
                      <Text
                        x={g.posX}
                        y={g.posY + r - 8}
                        width={g.width}
                        align="center"
                        text={t.name}
                        fontSize={12}
                        fontStyle="bold"
                        fill="#111"
                        listening={false}
                      />
                    </React.Fragment>
                  );
                }
                if (t.shape === 'ellipse') {
                  return (
                    <React.Fragment key={t.id}>
                      <Ellipse
                        {...common}
                        x={g.posX + g.width / 2}
                        y={g.posY + g.height / 2}
                        radiusX={g.width / 2}
                        radiusY={g.height / 2}
                        fill="#fff"
                        stroke={selectedTableId === t.id ? '#2563eb' : '#374151'}
                        strokeWidth={2}
                        onDragEnd={(evt: Konva.KonvaEventObject<DragEvent>) => {
                          const node = evt.target as Konva.Ellipse;
                          updateLocalTable(
                            t.id,
                            geomPatch(layoutMode, {
                              posX: Math.round(node.x() - g.width / 2),
                              posY: Math.round(node.y() - g.height / 2),
                            })
                          );
                        }}
                      />
                      <Text
                        x={g.posX}
                        y={g.posY + g.height / 2 - 8}
                        width={g.width}
                        align="center"
                        text={t.name}
                        fontSize={12}
                        fontStyle="bold"
                        fill="#111"
                        listening={false}
                      />
                    </React.Fragment>
                  );
                }
                if (t.shape === 'diamond') {
                  const size = Math.max(g.width, g.height);
                  const radius = size / Math.SQRT2;
                  return (
                    <React.Fragment key={t.id}>
                      <RegularPolygon
                        {...common}
                        x={g.posX + size / 2}
                        y={g.posY + size / 2}
                        sides={4}
                        radius={radius}
                        fill="#fff"
                        stroke={selectedTableId === t.id ? '#2563eb' : '#374151'}
                        strokeWidth={2}
                        onDragEnd={(evt: Konva.KonvaEventObject<DragEvent>) => {
                          const node = evt.target;
                          updateLocalTable(
                            t.id,
                            geomPatch(layoutMode, {
                              posX: Math.round(node.x() - size / 2),
                              posY: Math.round(node.y() - size / 2),
                            })
                          );
                        }}
                      />
                      <Text
                        x={g.posX}
                        y={g.posY + size / 2 - 8}
                        width={size}
                        align="center"
                        text={t.name}
                        fontSize={12}
                        fontStyle="bold"
                        fill="#111"
                        listening={false}
                      />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={t.id}>
                    <Rect
                      {...common}
                      width={g.width}
                      height={g.height}
                      cornerRadius={t.shape === 'roundRect' ? 12 : 4}
                      fill="#fff"
                      stroke={selectedTableId === t.id ? '#2563eb' : '#374151'}
                      strokeWidth={2}
                    />
                    <Text
                      x={g.posX}
                      y={g.posY + g.height / 2 - 8}
                      width={g.width}
                      align="center"
                      text={t.name}
                      fontSize={12}
                      fontStyle="bold"
                      fill="#111"
                      listening={false}
                    />
                  </React.Fragment>
                );
              })}
              <Transformer
                ref={transformerRef as React.RefObject<Konva.Transformer>}
                rotateEnabled
                enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
              />
            </Layer>
          </Stage>
          {selectedTableId != null && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={() => {
                const t = localTables.find((x) => x.id === selectedTableId);
                if (t && activeArea) openTable(t, activeArea.name);
              }}
            >
              Edit selected table details
            </button>
          )}
        </div>
      )}

      {/* Phone fallback list with position fields via edit sheet */}
      <div className="pos-floor-mobile-list" data-testid="pos-floor-mobile-list">
        {activeArea?.seatingMode === 'tables' &&
          localTables.map((t) => {
            const g = viewGeom(t, layoutMode);
            return (
              <button
                type="button"
                key={t.id}
                className="pos-floor-table-row"
                onClick={() => activeArea && openTable(t, activeArea.name)}
              >
                <span className="pos-floor-table-row__name">{t.name}</span>
                <span className="pos-floor-table-row__seats">
                  {t.seats} seats · {Math.round(g.posX)},{Math.round(g.posY)}
                </span>
                {!t.active && <span className="pos-floor-chip pos-floor-chip--mute">Inactive</span>}
                <span aria-hidden>›</span>
              </button>
            );
          })}
        {activeArea?.seatingMode === 'tables' &&
          decorElements.map((d) => {
            const g = viewGeom(d, layoutMode);
            return (
              <div key={`decor-row-${d.id}`} className="pos-floor-table-row pos-floor-decor-row">
                <span className="pos-floor-table-row__name">{d.name}</span>
                <span className="pos-floor-table-row__seats">
                  Decor · {d.decorType} · {Math.round(g.posX)},{Math.round(g.posY)}
                </span>
                <span className="pos-floor-chip pos-floor-chip--mute">Non-seatable</span>
              </div>
            );
          })}
      </div>

      {edit && (
        <div
          className="pos-floor-edit-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEdit(null);
          }}
        >
          <div className="pos-floor-edit-sheet">
            <h3>
              {edit.kind === 'new-area' && 'Add area'}
              {edit.kind === 'area' && 'Edit area'}
              {edit.kind === 'new-table' && `Add table · ${edit.areaName}`}
              {edit.kind === 'table' && `Edit table · ${edit.areaName}`}
            </h3>

            {(edit.kind === 'new-table' || edit.kind === 'table') && (
              <p className="text-muted" style={{ marginTop: -4, marginBottom: 12, fontSize: 13 }}>
                Editing {layoutMode === 'handheld' ? 'Handheld' : 'Station'} layout coordinates
              </p>
            )}

            <div className="mb-3">
              <label className="form-label" htmlFor="pos-floor-name">
                Name
              </label>
              <input
                id="pos-floor-name"
                className="form-control"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={busy}
              />
            </div>

            {(edit.kind === 'new-area' || edit.kind === 'area') && (
              <div className="mb-3">
                <label className="form-label" htmlFor="pos-floor-mode">
                  Seating mode
                </label>
                <select
                  id="pos-floor-mode"
                  className="form-select"
                  value={draft.seatingMode}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      seatingMode: e.target.value as 'tables' | 'named_tabs',
                    })
                  }
                  disabled={busy}
                >
                  <option value="tables">Tables</option>
                  <option value="named_tabs">Named tabs (no tables)</option>
                </select>
              </div>
            )}

            {(edit.kind === 'new-table' || edit.kind === 'table') && (
              <>
                <div className="mb-3">
                  <label className="form-label" htmlFor="pos-floor-seats">
                    Seats
                  </label>
                  <input
                    id="pos-floor-seats"
                    className="form-control"
                    type="number"
                    min={1}
                    value={draft.seats}
                    onChange={(e) => setDraft({ ...draft, seats: e.target.value })}
                    disabled={busy}
                  />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-x">
                      X
                    </label>
                    <input
                      id="pos-floor-x"
                      className="form-control"
                      type="number"
                      value={draft.posX}
                      onChange={(e) => setDraft({ ...draft, posX: e.target.value })}
                      disabled={busy}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-y">
                      Y
                    </label>
                    <input
                      id="pos-floor-y"
                      className="form-control"
                      type="number"
                      value={draft.posY}
                      onChange={(e) => setDraft({ ...draft, posY: e.target.value })}
                      disabled={busy}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-w">
                      Width
                    </label>
                    <input
                      id="pos-floor-w"
                      className="form-control"
                      type="number"
                      min={16}
                      value={draft.width}
                      onChange={(e) => setDraft({ ...draft, width: e.target.value })}
                      disabled={busy}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-h">
                      Height
                    </label>
                    <input
                      id="pos-floor-h"
                      className="form-control"
                      type="number"
                      min={16}
                      value={draft.height}
                      onChange={(e) => setDraft({ ...draft, height: e.target.value })}
                      disabled={busy}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-shape">
                      Shape
                    </label>
                    <select
                      id="pos-floor-shape"
                      className="form-select"
                      value={draft.shape}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          shape: e.target.value as FloorTableShape,
                        })
                      }
                      disabled={busy}
                    >
                      <option value="rect">Rectangle</option>
                      <option value="roundRect">Rounded</option>
                      <option value="circle">Circle</option>
                      <option value="ellipse">Ellipse</option>
                      <option value="diamond">Diamond</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label" htmlFor="pos-floor-rot">
                      Rotation
                    </label>
                    <input
                      id="pos-floor-rot"
                      className="form-control"
                      type="number"
                      value={draft.rotation}
                      onChange={(e) => setDraft({ ...draft, rotation: e.target.value })}
                      disabled={busy}
                    />
                  </div>
                </div>
              </>
            )}

            {(edit.kind === 'area' || edit.kind === 'table') && (
              <div className="pos-floor-edit-checks">
                <label className="pos-floor-edit-check">
                  <input
                    type="checkbox"
                    checked={draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    disabled={busy}
                  />
                  Active
                </label>
              </div>
            )}

            <div className="pos-floor-edit-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setEdit(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={busy}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosFloorAdmin;
